import React from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';
import type { PayoutAccountInfo } from './PayoutDialog.types';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useMount } from '@shared/utils/hooks/useMount';
import { pxToRem } from '@withjoy/joykit/theme';

type AllOrNothingAmountStepProps = Readonly<{
  payoutAccountInfo: PayoutAccountInfo;
  onContinue: () => void;
  onPageViewed?: () => void;
}>;

export const AllOrNothingAmountStep: React.FC<AllOrNothingAmountStepProps> = ({ payoutAccountInfo, onContinue, onPageViewed }) => {
  const { tq } = useTranslation('cashFundsAdmin');
  const { formatMinorUnitsCurrency } = useCurrencyFormatter();

  const currencyCode = payoutAccountInfo.balance?.available.currencyCode ?? payoutAccountInfo.currencyCode ?? DEFAULT_CURRENCY;
  const availableInMinorUnits = payoutAccountInfo.balance?.available.valueInMinorUnits ?? 0;

  useMount(() => {
    onPageViewed?.();
  });

  return (
    <Flex
      flexDirection="column"
      flex={1}
    >
      <TextV2
        textAlign="center"
        fontSize={pxToRem(24)}
        fontWeight={600}
        color="mono14"
        letterSpacing="-0.024em"
        lineHeight="1.3"
        marginBottom={6}
      >
        {tq`Transfer to bank`}
      </TextV2>

      <Flex
        flexDirection="column"
        alignItems="center"
        flex={1}
        justifyContent="center"
      >
        <Flex
          flexDirection="column"
          alignItems="center"
        >
          <TextV2
            textAlign="center"
            fontSize={pxToRem(15)}
            fontWeight={600}
            color="mono14"
            lineHeight="1.4"
            marginBottom={5}
          >
            {tq`You'll transfer your full available balance.`}
          </TextV2>
          <TextV2
            fontSize={pxToRem(13)}
            fontWeight={700}
            color="mono8"
            textTransform="uppercase"
            letterSpacing="0.05em"
            marginBottom={pxToRem(10)}
          >
            {tq`Available balance`}
          </TextV2>
          <TextV2
            fontSize={pxToRem(52)}
            fontWeight={700}
            color="mono14"
            letterSpacing="-0.025em"
            lineHeight="1"
          >
            {formatMinorUnitsCurrency(availableInMinorUnits, currencyCode)}
          </TextV2>
        </Flex>

        <TextV2
          textAlign="center"
          fontSize={pxToRem(15)}
          color="mono10"
          lineHeight="1.4"
          marginTop={6}
        >
          {tq`New balance:`}{' '}
          <Box
            as="span"
            fontWeight={600}
            color="mono14"
          >
            {formatMinorUnitsCurrency(0, currencyCode)}
          </Box>
        </TextV2>
      </Flex>

      <ButtonV2
        onClick={onContinue}
        size="lg"
        intent="neutral"
        shape="rounded"
        fullWidth
        marginTop={6}
        disabled={availableInMinorUnits <= 0}
      >
        {tq`Continue`}
      </ButtonV2>
    </Flex>
  );
};
